// Ch78 · who is working what — one strip above the Leads/Clients tabs.
//
// One tile per locator: active clients, the stage split as coloured counts,
// and how many of theirs have never been contacted. Tap a tile to see that
// locator's clients. The "Unassigned" tile is the gap a manager closes.

import React from "react";
import { Avatar, Box, ButtonBase, Chip, Stack, Tooltip, Typography } from "@mui/material";

import type { Agent, Client } from "./types";
import { statusConfig } from "./config";
import { WORK_STAGES, buildLocatorWorkload } from "../../lib/locatorWorkload";

export interface LocatorWorkloadStripProps {
  clients: Client[];
  agents?: Agent[];
  /** "unassigned" for the no-locator tile. */
  onPickAgent: (agentId: string) => void;
  selectedAgentId?: string | null;
}

export default function LocatorWorkloadStrip({ clients, agents = [], onPickAgent, selectedAgentId }: LocatorWorkloadStripProps) {
  const rows = React.useMemo(() => buildLocatorWorkload(clients, agents), [clients, agents]);
  if (rows.length === 0) return null;
  return (
    <Box data-testid="locator-workload" sx={{ mb: 2 }}>
      <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: 0.6, display: "block", mb: 0.5 }}>
        Who's working what
      </Typography>
      <Stack direction="row" spacing={1} sx={{ overflowX: "auto", pb: 0.5, scrollbarWidth: "none" }}>
        {rows.map((row) => {
          const selected = selectedAgentId === row.agentId;
          return (
            <ButtonBase
              key={row.agentId}
              onClick={() => onPickAgent(row.agentId)}
              data-testid={`locator-tile-${row.agentId}`}
              aria-pressed={selected}
              sx={{
                flexShrink: 0,
                textAlign: "left",
                borderRadius: 2,
                border: 1,
                borderColor: selected ? "primary.main" : "divider",
                bgcolor: selected ? "action.selected" : "background.paper",
                px: 1.5,
                py: 1,
                minWidth: 168,
                alignItems: "flex-start",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Stack spacing={0.5} sx={{ width: "100%" }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar src={row.avatarUrl} sx={{ width: 24, height: 24, fontSize: 11 }}>
                    {row.name.slice(0, 1)}
                  </Avatar>
                  <Typography variant="body2" fontWeight={700} noWrap sx={{ flex: 1 }}>
                    {row.name}
                  </Typography>
                  <Typography variant="body2" fontWeight={800} color={row.agentId === "unassigned" ? "warning.main" : "text.primary"}>
                    {row.total}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  {WORK_STAGES.filter((s) => row.byStage[s]).map((s) => (
                    <Tooltip key={s} title={statusConfig[s]?.label || s}>
                      <Stack direction="row" spacing={0.35} alignItems="center">
                        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: statusConfig[s]?.color || "text.disabled" }} />
                        <Typography variant="caption" color="text.secondary">
                          {row.byStage[s]}
                        </Typography>
                      </Stack>
                    </Tooltip>
                  ))}
                  {row.neverContacted > 0 && (
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label={`${row.neverContacted} never contacted`}
                      sx={{ height: 18, "& .MuiChip-label": { px: 0.6, fontSize: "0.62rem", fontWeight: 700 } }}
                    />
                  )}
                </Stack>
              </Stack>
            </ButtonBase>
          );
        })}
      </Stack>
    </Box>
  );
}
